<script lang="ts" setup>
import {computed, onMounted, Ref, ref, watch} from "vue";

import Swal from 'sweetalert2'
import Button from "@src/components/ui/inputs/Button.vue";
import Modal from "@src/components/ui/utils/Modal.vue";
import {ChevronDownIcon, XMarkIcon} from "@heroicons/vue/24/outline";
import {PhoneIcon, UserIcon} from "@heroicons/vue/24/solid";
import {PhoneObject, VueTelInput} from 'vue-tel-input'
import 'vue-tel-input/vue-tel-input.css'
import {useChatSessions} from "@src/composables/useChatSessions";

import SessionService from '@src/services/session';
import {useContacts} from '@src/composables/useContacts'
import {useChannels} from '@src/composables/useChannels'

import useStore from "@src/stores/store";
import {useChatSessionStore} from "@src/stores/chatSession";
import Channel from "@src/types/Channel";
import {useWAAPIStore} from "@src/stores/waapi";
import {permissionsHelper} from "@src/helpers/permissionsHelper";

const chatSessionComposable = useChatSessions()

const store = useStore()
const sessionStore = useChatSessionStore()

const {getAllContacts} = useContacts()
const {activeChannels} = useChannels()
const contacts = getAllContacts()
const channels = activeChannels()

const filter = ref('')
const current = ref<number | null>(null)

const selectedName = ref('')
const selectedPhone = ref('')

const phone = ref('')
const phoneE164 = ref('')
const apiPhone = ref('')

const showContacts = ref(false)
const selectedChannel = ref<Channel>()
const loading = ref(false)
const waapiStore = useWAAPIStore()
const permissions = permissionsHelper.all()

const selectedContactArea = ref(false)

const toggleContacts = () => {
    showContacts.value = !showContacts.value
}
const selectContact = (contact: any) => {
    selectedContactArea.value = true
    selectedName.value = contact.name
    selectedPhone.value = contact.token

    current.value = contact.id
    showContacts.value = false
}
const clearSelected = () => {
    selectedContactArea.value = false
    selectedName.value = ''
    selectedPhone.value = ''
}

const filteredContacts = computed(() => {
    const term = filter.value?.toLowerCase()

    const result = term
        ? contacts.value?.filter(contact =>
            contact.name?.toLowerCase().includes(term) ||
            contact.nickname?.toLowerCase().includes(term) ||
            contact.token?.toLowerCase().includes(term)
        )
        : contacts.value

    return result?.slice().sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
})

const props = defineProps<{
    open: boolean;
    closeModal: () => void;
}>()

const emits = defineEmits(['token'])

const modalTitle: Ref<HTMLElement | null> = ref(null)

const canStartConversation = computed(() => {
    return selectedChannel.value?.id.toString() && (selectedPhone.value !== '' || phoneIsValid.value)
})

const startConversation = async () => {
    if (!canStartConversation.value) return

    loading.value = true

    apiPhone.value = phoneE164.value.replace('+', '');
    if (selectedPhone.value !== '') {
        apiPhone.value = selectedPhone.value
    }

    const response = await SessionService.verifyStartConversation(apiPhone.value)

    if (!response) {
        await openNewSession(apiPhone.value, selectedChannel.value?.id.toString() || '');
        return
    }

    const {isConfirmed} = await Swal.fire({
        title: 'Conversa em andamento',
        text: 'Esse número já está em atendimento, deseja continuar?',
        icon: 'question',
        showCancelButton: true,
        reverseButtons: true,
        confirmButtonText: 'Sim, continuar',
        cancelButtonText: 'Cancelar'
    })

    if (isConfirmed) {
        await openNewSession(apiPhone.value, selectedChannel.value?.id.toString() || '');
        return
    }

    loading.value = false
}

const openNewSession = async (apiPhone: string, selectedChannel: string) => {
    loading.value = true

    const newSessionId = await SessionService.startConversation(apiPhone, selectedChannel)

    const session$ = chatSessionComposable.getSessionById(newSessionId)

    const stop = watch(
        session$,
        (session) => {
            if (!session) return

            sessionStore.clearSession()
            sessionStore.selectedCostumer = {
                id: 0,
                name: '',
                token: ''
            }
            // 👇 envia o dado PRONTO
            sessionStore.selectSession(session)

            loading.value = false
            props.closeModal()
            store.activeSidebarComponent = 'active'

            stop() // 👈 evita bugs e múltiplas execuções
        },
        {immediate: true}
    )
}


const phoneIsValid = computed(() => {
    return phone.value.replace(/\D/g, '').length >= 11
})

const handleInput = (formattedNumber: string, phoneObject: PhoneObject) => {
    // phoneObject.number contém o formato internacional (ex: +5511999998888)

    if (phoneObject.valid) {
        phoneE164.value = phoneObject.number;
    } else {
        // Opcional: limpa ou mantém o valor enquanto é inválido
        phoneE164.value = phoneObject.number || '';
    }
};

const initOfficialSession = async () => {
    if (!selectedChannel.value) {
        return
    }
    loading.value = true
    waapiStore.campaignForm.channel_id = selectedChannel.value.id
    waapiStore.selectedChannel = selectedChannel.value
    apiPhone.value = phoneE164.value.replace('+', '');
    if (selectedPhone.value !== '') {
        apiPhone.value = selectedPhone.value
    }
    emits('token', apiPhone.value)
    clearSelected()
    loading.value = false
    props.closeModal()
}

onMounted(() => {
    if (sessionStore.selectedCostumer.name) {
        selectContact(sessionStore.selectedCostumer)
    }
})
</script>

<template>
    <Modal :close-modal="props.closeModal" :open="props.open">
        <template v-slot:content>
            <div class="w-full md:w-120 rounded pt-5 bg-white dark:bg-gray-800 shadow-2xl dark:shadow-gray-800">
                <div class="flex justify-between items-center mb-6 px-5 ">
                    <p
                        id="modal-title"
                        ref="modalTitle"
                        class="heading-1 text-black/70 dark:text-white/70"
                        tabindex="0"
                    >
                        Iniciar Atendimento
                    </p>

                    <div
                        class="mx-2 cursor-pointer rounded-full p-3 text-brand-400 dark:text-white hover:bg-brand-300 hover:text-white transition-colors duration-300 ease-out"
                        @click="props.closeModal"
                    >
                        <XMarkIcon class="w-[20px] h-[20px] font-semibold"/>
                    </div>
                </div>

                <div class="w-full px-5 text-start ">
                    <p
                        id="modal-title"
                        class="heading-2 text-black/70 dark:text-white/70 mb-3"
                        tabindex="0"
                    >
                        Canal:
                    </p>

                    <select v-model="selectedChannel"
                            class="w-full p-3 text-sm px-3 border-1 border-gray-300 rounded-sm dark:text-white/70">
                        <option class="text-gray-500 dark:bg-gray-900" disabled value="">
                            Selecione um canal
                        </option>

                        <option
                            v-for="channel in channels"
                            :key="channel.id"
                            :value="channel"
                            class="text-gray-500 dark:bg-gray-900"
                        >
                            {{ channel.name }} - {{ channel.token }}
                        </option>
                    </select>
                </div>

                <div
                    v-if="selectedChannel?.type === 'waapi'"
                    class="mx-5 mb-5 flex items-start text-start gap-3 rounded-lg p-4 max-w-md"
                >
                    <div class="mt-0.5 flex-shrink-0">
                        <svg class="h-5 w-5 text-black dark:text-white"
                             fill="currentColor" viewBox="0 0 20 20"
                             xmlns="http://www.w3.org/2000/svg">
                            <path clip-rule="evenodd"
                                  d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                                  fill-rule="evenodd"/>
                        </svg>
                    </div>
                    <div>
                        <p class="text-sm font-semibold text-black dark:text-white">Canal Oficial</p>
                        <p class="mt-0.5 text-sm text-gray-600 dark:text-gray-300/80">
                            Esse número é um Canal Oficial. Para iniciar uma conversa será necessário enviar uma
                            campanha de mensagem.
                        </p>
                    </div>
                </div>

                <div
                    :class="showContacts ? 'max-h-[900px]' : 'max-h-[260px]'"
                    class="px-5 min-h-50 text-start mt-4 overflow-hidden transition-[max-height] duration-600 ease-[cubic-bezier(0.16,1,0.3,1)]"
                >
                    <p id="modal-title" ref="modalTitle" class="heading-2 text-black/70 dark:text-white/70 mb-3"
                       tabindex="0">
                        Contato:
                    </p>

                    <VueTelInput
                        v-if="!selectedContactArea"
                        v-model="phone"
                        :inputOptions="{ placeholder: 'Digite o telefone'}"
                        @on-input="handleInput"
                    />

                    <div v-if="selectedContactArea"
                         class="w-full border-1 flex-col border-gray-300 px-3 rounded-sm bg-gray-100 dark:bg-gray-700">
                        <div class="flex w-full justify-between px-2 pt-3 items-center">
                            <div class="flex items-center text-gra-800">
                                <UserIcon class="w-[16px] h-[16px] font-semibold text-brand-500"/>
                                <span class="text-gray-600 dark:text-gray-200">:</span>
                                <p class="heading-2 ml-2 text-gray-600 dark:text-gray-200">{{
                                        selectedName
                                    }}</p>
                            </div>
                            <div v-if="!sessionStore.selectedCostumer.name" class="cursor-pointer">
                                <XMarkIcon
                                    class="w-[20px] h-[20px] font-semibold text-gray-500 dark:text-gray-200"
                                    @click="clearSelected"/>
                            </div>
                        </div>
                        <div class="flex items-center p-2 pb-3">
                            <PhoneIcon class="w-[16px] h-[16px] font-semibold text-green-600"/>
                            <span class="text-gray-600 dark:text-gray-200">:</span>
                            <p class="heading-2 ml-2 text-gray-500 dark:text-gray-200">{{ selectedPhone }}</p>
                        </div>
                    </div>

                    <div v-if="!sessionStore.selectedCostumer.name"
                         class="text-start flex items-center justify-between mt-4 w-full">
                        <div
                            class="flex items-center justify-between bg-brand-400 p-3 rounded-md text-white w-full cursor-pointer hover:bg-brand-500 transition duration-200"
                            @click="toggleContacts"
                        >
                            <div class="flex items-center justify-between w-full px-2">
                                <UserIcon class="w-[16px] h-[16px] font-semibold"/>
                                <p class="heading-2 ml-3">Agenda de Contatos</p>

                                <ChevronDownIcon
                                    :class="{ 'rotate-90': showContacts }"
                                    class="w-[16px] h-[16px] font-semibold transition-transform duration-300"
                                />
                            </div>
                        </div>
                    </div>

                    <transition
                        enter-active-class="transition-all duration-400 ease-out"
                        enter-from-class="opacity-0 scale-95"
                        enter-to-class="opacity-100 scale-100"
                        leave-active-class="transition-all duration-400 ease-in"
                        leave-from-class="opacity-100 scale-100"
                        leave-to-class="opacity-0 scale-95"
                    >
                        <div v-show="showContacts" class="flex flex-col divide-y">
                            <div class="flex flex-col divide-y mt-3">
                                <input
                                    v-model="filter"
                                    class="w-full p-3 py-3 border-2 border-gray-300 rounded-sm text-gray-500 placeholder:text-gray-400 text-sm font-semibold
                                            focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-300"
                                    placeholder="Buscar nome ou apelido"
                                    type="text"
                                /></div>
                            <div
                                class="overflow-auto relative mx-auto bg-white dark:bg-gray-800 flex flex-col divide-y divide dark:divide-gray-400 w-full max-h-[240px]">
                                <div
                                    v-for="contact in filteredContacts"
                                    :key="contact.id"
                                    :class="{ 'bg-neutral-200 text-neutral-500 ring-0 dark:bg-gray-600 dark:text-white': current === contact.id }"
                                    class="flex py-3 px-2 flex-col cursor-pointer select-none hover:bg-neutral-100 hover:text-neutral-500 dark:hover:bg-gray-700"
                                    @click="selectContact(contact)"
                                >
                                    <div class="flex">
                                        <strong class="text-slate-900 dark:text-white text-sm font-medium">
                                            {{ contact.name }}
                                        </strong>

                                        <span v-if="contact.nickname"
                                              class="text-slate-500 text-sm dark:text-gray-300 font-medium"> &nbsp;-&nbsp; {{
                                                contact.nickname
                                            }}</span>
                                    </div>

                                    <span class="text-slate-500 dark:text-gray-400 text-sm font-medium"> {{
                                            contact.token
                                        }}</span>
                                </div>

                                <div
                                    v-if="filteredContacts?.length === 0"
                                    class="p-3 text-sm text-gray-400"
                                >
                                    Nenhum contato encontrado
                                </div>
                            </div>
                        </div>
                    </transition>
                </div>


                <div class="flex justify-center">
                    <Button
                        v-if="selectedChannel?.type !== 'waapi'"
                        :class="canStartConversation ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-400 cursor-not-allowed opacity-60'"
                        :disabled="!canStartConversation"
                        :loading="loading"
                        class="p-3 mb-6 mt-2 font-bold text-white w-[60%]"
                        @click="startConversation">
                        Iniciar conversa
                    </Button>
                    <Button
                        v-else
                        :disabled="!permissions.campaign_create || (!phoneIsValid && !selectedPhone)"
                        :loading="loading"
                        :title="!permissions.campaign_create ? 'Você não possui permissão para enviar campanhas individuais' : ''"
                        class="p-3 mb-6 mt-4 font-bold text-white w-[60%] bg-indigo-600 hover:bg-indigo-700 disabled:opacity-25 disabled:cursor-not-allowed"
                        @click="initOfficialSession">
                        Enviar Campanha
                    </Button>
                </div>
            </div>
        </template>
    </Modal>
</template>

<style>
.vti__input.vti__phone {
    background-color: #ffffff;
    color: #111827;
}

.dark .vti__input.vti__phone {
    background-color: #1f2937;
    color: #ffffff;
}
</style>